import { useMemo, useState } from "react";
import { MUSCLE_GROUP_LABELS } from "../../api/types.ts";
import { FetchedExercise } from "../../util/exercise.tsx";
import { useDialog } from "../DialogManager.tsx";
import { DropdownItem } from "./DropdownItem.tsx";
import { TextInput } from "../inputs/TextInput.tsx";
import { Dropdown } from "./Dropdown.tsx";
import { FaPlus } from "react-icons/fa";

export const AddExerciseButton = ({
  availableExercises,
  isEditable,
  onAddExercise,
}: {
  availableExercises: Map<string, FetchedExercise>;
  isEditable: boolean;
  onAddExercise: (exerciseId: string) => void;
}) => {
  const { openDialog } = useDialog();

  const [isAddExerciseOpen, setIsAddExerciseOpen] = useState(false);
  const [exercisesSearchQuery, setExercisesSearchQuery] = useState("");

  const queriedExercises: FetchedExercise[] = useMemo(() => {
    const sortedExercises = Array.from(availableExercises.values()).sort(
      (a, b) => a.exercise?.name.localeCompare(b.exercise?.name || "") || 0,
    );

    if (exercisesSearchQuery === "") {
      return sortedExercises;
    }

    return sortedExercises.filter((fetched: FetchedExercise) => {
      if (!fetched.exercise) {
        return false;
      }

      return (
        fetched.exercise.name
          .toLowerCase()
          .includes(exercisesSearchQuery.toLowerCase()) ||
        fetched.exercise.primary_muscle_groups.some((muscle) =>
          MUSCLE_GROUP_LABELS[muscle.muscle_group]
            .toLowerCase()
            .includes(exercisesSearchQuery.toLowerCase()),
        ) ||
        fetched.exercise.secondary_muscle_groups.some((muscle) =>
          MUSCLE_GROUP_LABELS[muscle.muscle_group]
            .toLowerCase()
            .includes(exercisesSearchQuery.toLowerCase()),
        ) ||
        fetched.exercise.description
          ?.toLowerCase()
          .includes(exercisesSearchQuery.toLowerCase())
      );
    });
  }, [exercisesSearchQuery, availableExercises]);

  return availableExercises.size > 0 && isEditable ? (
    <Dropdown
      trigger={
        <button
          className="p-2 rounded bg-gray-50 my-3 text-gray-600 hover:bg-secondary hover:text-white transition-colors w-full"
          disabled={availableExercises.size === 0}
        >
          <FaPlus
            className="inline-block transition-transform duration-300"
            style={{
              transform: isAddExerciseOpen ? "rotate(45deg)" : "rotate(0deg)",
            }}
          />
        </button>
      }
      onOpenChange={setIsAddExerciseOpen}
      alignment="center"
    >
      <DropdownItem key={"search"} onClick={() => null}>
        {availableExercises.size > 3 && (
          <TextInput
            id={"search"}
            placeholder="Search..."
            value={exercisesSearchQuery}
            onChange={(e) => setExercisesSearchQuery(e.target.value)}
          />
        )}
      </DropdownItem>
      {queriedExercises.map((fetched) => (
        <DropdownItem
          key={fetched.exercise?.id}
          onClick={() => onAddExercise(fetched.exercise?.id || "")}
        >
          {fetched.exercise?.name}
        </DropdownItem>
      ))}
    </Dropdown>
  ) : (
    availableExercises.size === 0 && (
      <p className="text-gray-500 text-sm text-center my-3">
        There are no exercises in your library.{" "}
        <button
          className="text-secondary underline"
          onClick={() => {
            openDialog("create-exercise");
          }}
        >
          You can create an exercise
        </button>{" "}
        to add to your workout.
      </p>
    )
  );
};
